// Libs
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import axios from "axios";
import { polyfill } from "smoothscroll-polyfill";
import { pdfjs } from "react-pdf";

// CSS
import "./styles/main.scss";

// Root
import Root from "./root";

// Store
import store from "./store";

// Env
import env from "./env";
import { changeAuth, reloadUser, setToken } from "./containers/Auth/actions";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// Call smoothscroll polyfill
polyfill();

// Create instance server-node
export let serverAPI = axios.create({
  headers: {
    userid: store.getState().auth.user.id,
    groupid: store.getState().auth.user.groupId,
  },
  withCredentials: true,
});

export let reportAPI = axios.create({
  headers: {
    userid: store.getState().auth.user.id,
    groupid: store.getState().auth.user.groupId,
    "content-type": "application/json",
  },
});

export let searchAPI = axios.create({
  headers: {
    userid: store.getState().auth.user.id,
    groupid: store.getState().auth.user.groupId,
  },
});

// If is in development
if (env === "production") {
  serverAPI.defaults.baseURL = "https://server.semprocesso.com.br";
  reportAPI.defaults.baseURL = "https://report-sql.semprocesso.com.br";
  searchAPI.defaults.baseURL = "https://search.semprocesso.com.br";
  axios.defaults.baseURL = "https://spbe.semprocesso.com.br";
} else {
  serverAPI.defaults.baseURL = "http://localhost:3001";
  // serverAPI.defaults.baseURL = 'http://192.168.2.107:3001'
  // serverAPI.defaults.baseURL = 'https://server.semprocesso.com.br'
  reportAPI.defaults.baseURL = "https://report-sql.semprocesso.com.br";
  // reportAPI.defaults.baseURL = 'http://localhost:3003'
  searchAPI.defaults.baseURL = "https://search.semprocesso.com.br";
  // axios.defaults.baseURL = 'http://localhost:8080'
  axios.defaults.baseURL = "https://spbe.semprocesso.com.br";
}

// // Sempre adicionar access token aos headers
serverAPI.interceptors.request.use((config) => {
  const token = store.getState().auth.token;
  if (token) {
    config.headers["x-auth-token"] = token;
  }
  return config;
});

reportAPI.interceptors.request.use((config) => {
  const token = store.getState().auth.token;
  if (token) {
    config.headers["x-auth-token"] = token;
  }
  return config;
});

searchAPI.interceptors.request.use((config) => {
  const token = store.getState().auth.token;
  if (token) {
    config.headers["x-auth-token"] = token;
  }
  return config;
});

let isRefreshingToken = false;
let refreshTokenPromise = null;

// Função centralizada para renovar token
export const refreshAuthToken = async () => {
  // Se já está renovando, retornar a Promise existente
  if (isRefreshingToken && refreshTokenPromise) {
    return refreshTokenPromise;
  }

  isRefreshingToken = true;

  refreshTokenPromise = serverAPI
    .post("/user/refresh-token")
    .then((response) => {
      const { token, user } = response.data;
      store.dispatch(setToken(token));
      store.dispatch(changeAuth(true));
      store.dispatch(reloadUser(true));
      isRefreshingToken = false;
      refreshTokenPromise = null;

      return { token, user };
    })
    .catch((error) => {
      isRefreshingToken = false;
      refreshTokenPromise = null;
      localStorage.removeItem("token");
      throw error;
    });

  return refreshTokenPromise;
};


let failedQueue = [];
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

serverAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const tokenMessages = ["Token expired or invalid"]

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      tokenMessages.includes(error.response?.data?.message)
    ) {
      // Se já está renovando, enfileirar esta requisição
      if (isRefreshingToken) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["x-auth-token"] = token;
            return serverAPI(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;

      try {
        const { token } = await refreshAuthToken();

        // Processar fila de requisições pendentes
        processQueue(null, token);

        // Retentar a requisição original com novo token
        originalRequest.headers["x-auth-token"] = token;
        return serverAPI(originalRequest);
      } catch (refreshError) {
        // Falhou ao renovar - fazer logout
        processQueue(refreshError, null);
        document.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Render the application
ReactDOM.render(
  // Provide the store to the whole app
  <Provider store={store}>
    <Root />
  </Provider>,
  document.getElementById("root")
);
